import {
  HTTPMethods,
  RequestExecutor,
} from "../../api-helpers/RequestExecutor";
import { cloneDeep } from "lodash";
import {
  getCampaignEndPoints,
  getChatbotEndpoints,
  getCloudFunctionEndpoints,
  getFastifyEndpoints,
  getIntegrationsEndpoints,
  isProd,
  getCRMEndpoints,
} from "../../config";
import { FirebaseService } from "../firebase/FirebaseService";
import { collection, getDocs, limit, query } from "firebase/firestore";
import Fuse from "fuse.js";
import { SyncTemplateResponse } from "../../utilities/flowBuilder.utility";
import { captureErrorToSentry } from "../../utilities/sentryHelper";
import botStore from "../../state/store";
import { CHANNELS } from "../../utilities/login.utility";
import { ApplicationType } from "../../app/messageBlock/Constants";
export interface ProductData {
  catalogId: string;
  productId: string;
  image: string;
  name: string;
  variantId: string;
  custom: string;
}

export const isStoreIdValid = async (storeId: string): Promise<any> => {
  const url = isProd
    ? "https://bikapi.bikayi.app/growth/bikStoreApiFunctions-getBikStoreById"
    : "https://api.staging.bik.ai/growth/bikStoreApiFunctions-getBikStoreById";

  const response: any = await RequestExecutor.fetch({
    apiPath: url,
    apiMethod: HTTPMethods.POST,
    requestBody: {
      storeId: storeId,
    },
  });

  if (response?.data && response?.data?.status === 200) {
    return response?.data.store;
  }

  return {};
};

export const fetchProductsBySearchText = async (
  storeId: string,
  searchText: string,
  applicationType: string = ApplicationType.BIK
) => {
  const url =
    getIntegrationsEndpoints() +
    "/integrationsApiFunctions-executeIntegrationApi";

  const firebaseService = new FirebaseService(
    () => { },
    () => { }
  );

  const defaultProducts: any = [];
  const productData: ProductData[] = [];

  const parseProducts = async (items: any) => {
    items?.forEach((productInfo: any) => {
      const combinations = productInfo?.combinations;

      combinations?.forEach((combination: any) => {
        const variantInfo: ProductData = {
          catalogId: "",
          productId: "",
          image: "",
          name: "",
          variantId: "",
          custom: "",
        };

        if (combination.productId) {
          variantInfo.productId = combination.productId;
        }

        if (combination.id) {
          variantInfo.variantId = combination.id;
        }

        if (combination.custom) {
          variantInfo.custom = combination.custom;
        }

        if (productInfo.name) {
          variantInfo.name = productInfo.name;
        }

        if (productInfo.image) {
          variantInfo.image = productInfo.image;
        }

        productData.push(variantInfo);
      });
    });
  };

  try {
    if (!searchText.trim()) {
      const database = firebaseService.getDatabase();
      const colRef = collection(database, "store-new", storeId, "items");
      const queryRef = query(colRef, limit(10));
      const querySnapshot = await getDocs(queryRef);
      if (!querySnapshot.empty) {
        querySnapshot.docs.forEach((doc) => {
          defaultProducts.push({ ...doc.data(), id: doc.id } as any);
        });
      }

      await parseProducts(defaultProducts);
      return { productData };
    }
  } catch (err) {
    captureErrorToSentry(err, "Error in parseProducts 1");
    return { productData: [] };
  }

  try {
    const response: any = await RequestExecutor.fetch({
      apiPath: url,
      apiMethod: HTTPMethods.POST,
      requestBody: {
        storeId: storeId,
        partner: "SHOPIFY",
        code: "searchProducts",
        payload: {
          query: searchText,
        },
        appType: applicationType
      },
    });
    const responseJson = response.data;
    if (responseJson.status === 200) {
      await parseProducts(responseJson.response?.products || []);
      return { productData };
    }
    return { productData: [] };
  } catch (err) {
    captureErrorToSentry(err, "Error in parseProducts 2");
    return { productData: [] };
  }
};

export const fetchProductById = async (storeId: string, productId: string) => {
  const url =
    getFastifyEndpoints() +
    `/storeApiFunctions-getProductV2/${storeId}/${"0"}/${productId}`;

  try {
    const res: any = await RequestExecutor.fetch({
      apiPath: url,
      apiMethod: HTTPMethods.GET,
    });
    const responseJson = res.data;
    const productData: ProductData[] = [];
    const product = responseJson.product;
    if (Object.keys(product).length) {
      if (product?.combinations?.length) {
        product?.combinations?.forEach((variant: any) => {
          if (
            !variant?.id ||
            !product?.id ||
            !(variant?.custom || variant?.name) ||
            !product?.name
          ) {
            return;
          }
          const variantInfo: ProductData = {
            catalogId: "",
            productId: "",
            image: "",
            name: "",
            variantId: "",
            custom: "",
          };
          variantInfo.catalogId = product?.catalogId || "";
          variantInfo.productId = product?.id;
          variantInfo.image = product?.image || "";
          variantInfo.name = product?.name;
          variantInfo.variantId = variant?.id;
          if (variant?.custom) {
            variantInfo["custom"] = variant.custom;
          }
          if (variant?.name) {
            variantInfo["custom"] = variant.name;
          }
          variantInfo.custom = variant?.custom;
          productData.push(variantInfo);
        });
      }
      return { productData };
    } else {
      return { productData: [] };
    }
  } catch (err) {
    captureErrorToSentry(err, "Error in fetchProductById");
    return { productData: [] };
  }
};

export const fetchAllCatalogs = async (storeId: string) => {
  const url = getFastifyEndpoints() + "/storeApiFunctions-getAllCatalog";

  try {
    const response: any = await RequestExecutor.fetch({
      apiPath: url,
      apiMethod: HTTPMethods.POST,
      requestBody: {
        storeId: storeId,
      },
    });

    if (response?.data) {
      const responseJson = response.data;
      return responseJson.filter((item: any) => item?.id && item.id !== 0);
    } else {
      return [];
    }
  } catch (err) {
    captureErrorToSentry(err, "Error in fetchAllCatalogs");
    return [];
  }
};

export const fetchMicroflowConf = async (
  conf_name: string,
  storeId: string
) => {
  const myHeaders = new Headers();
  myHeaders.append("Content-Type", "application/json");

  const url = `${getChatbotEndpoints()}/get-microflow-template/${conf_name}`;

  try {
    const response: any = await RequestExecutor.fetch({
      apiPath: url,
      apiMethod: HTTPMethods.GET,
      requestBody: {
        store_id: storeId,
      },
    });
    if (response.data && response.data?.data) {
      return response.data.data;
    } else {
      return {};
    }
  } catch (err) {
    captureErrorToSentry(err, "Error in fetchMicroflowConf");
    return {};
  }
};

export const updateKeywordCache = async (
  storeId: string,
  keywords: any,
  reset: boolean = false
) => {
  const myHeaders = new Headers();
  myHeaders.append("Content-Type", "application/json");

  const body: any = {
    storeId,
    keywords,
  };

  if (reset) {
    body["delete"] = true;
  }

  const url = `${getChatbotEndpoints()}/update-keywords-cache`;

  try {
    await RequestExecutor.fetch({
      apiPath: url,
      apiMethod: HTTPMethods.POST,
      requestBody: body,
    });
  } catch (err: any) {
    captureErrorToSentry(err, "Error in updateKeywordCache");
  }
};

export const parseMediaType = async (url: string, storeId: string) => {
  try {
    const res: any = await RequestExecutor.fetch({
      apiPath: `${getCampaignEndPoints()}/whatsappApiFunctions-getFileInfoFromUrl`,
      apiMethod: HTTPMethods.POST,
      requestBody: {
        url,
        storeId: storeId,
      },
    });

    if (res.data) {
      return res.data;
    } else {
      return res;
    }
  } catch (err: any) {
    return err;
  }
};

export const genConfigOperatorValues = (messageConf: any) => {
  const config_operator_values: any = {};
  const operator_values = messageConf.conditions?.[0]?.operator_values || {};
  if (messageConf["message_template"]["buttonsList"]) {
    messageConf["message_template"]["buttonsList"].forEach(
      (el: any, index: number) => {
        config_operator_values["+" + el.buttonKey] = cloneDeep(
          operator_values[el.buttonKey],
        );
        if (el.ctaValue) {
          config_operator_values["+" + el.buttonKey].isConnectable = false;
        }
      }
    );
  }

  return config_operator_values;
};

export const testEvalExpression = async (
  expression: string,
  id: string,
  storeId: string
): Promise<{ id: string; status: boolean; type?: string }> => {
  try {
    const response: any = await RequestExecutor.fetch({
      apiPath: `${getChatbotEndpoints()}/test-expression`,
      apiMethod: HTTPMethods.POST,
      requestBody: {
        expression,
        flow_id: "csad13",
        store_id: storeId,
      },
    });

    if (response.error) {
      return { id: id, status: false };
    }

    if (response.data && response.data.valid) {
      if (response.data.result) {
        // Used for Handling type check | If its an array we have type as list or actual type
        return { id: id, status: true, type: Array.isArray(response.data.result) ? 'list' : typeof response.data.result };
      } else {
        return { id: id, status: true };
      }
    }

    return { id: id, status: false };
  } catch (err) {
    return { id: id, status: false };
  }
};

export const getCurrentUser = async (storeId: string, email: string) => {
  try {
    const response: any = await RequestExecutor.fetch({
      apiPath: `${getCRMEndpoints()}/userApiFunctions-getUsers`,
      apiMethod: HTTPMethods.POST,
      requestBody: {
        storeId: storeId,
        email: email,
      },
    });

    if (response.status === 200) {
      if (response.data.users.length > 0) {
        return response.data.users[0];
      }
    }

    return {};
  } catch (err) {
    captureErrorToSentry(err, "Error in getCurrentUser");
    return {};
  }
};

export const compileFlow = async (
  storeId: string,
  flowId: string,
  reactFlow?: any,
  flowConfig?: any,
  testMode?: boolean,
  status?: boolean,
  sunset?: boolean,
  startAFlow?: any,
  agentId?: number | string | null,
  flowMeta?: any,
  triggerType?: any,
  dndEnabled?: boolean,
  forcePublish?: boolean
) => {
  try {
    const response: any = await RequestExecutor.fetch({
      apiPath: `${getChatbotEndpoints()}/publish-flow-with-compilation`,
      apiMethod: HTTPMethods.POST,
      requestBody: {
        store_id: storeId,
        flow_id: flowId,
        react_flow: reactFlow || {},
        flow_config: flowConfig || {},
        is_test_mode: testMode,
        status: status,
        is_sunset: sunset,
        start_a_flow: startAFlow,
        agent_id: agentId,
        force_publish: forcePublish,
      },
    });

    if (response) {
      return response.data;
    }
    return {};
  } catch (err) {
    captureErrorToSentry(err, "Error in compileFlow");
    return {};
  }
};

export const fetchAllProducts = async (storeId: string) => {
  const firebaseService = new FirebaseService(
    () => { },
    () => { }
  );
  const products = await firebaseService.fetchAllProducts(storeId);
  return products;
};

export const searchProductsById = async (query: string, products: any[]) => {
  const options = {
    keys: [
      {
        name: "uniqueId",
        weight: 1,
      },
    ],
    useExtendedSearch: true,
    minMatchCharLength: query.length,
    findAllMatches: false,
    shouldSort: true,
  };
  const fuseIndex = Fuse.createIndex(options.keys, products);
  const fuse = new Fuse(products, options, fuseIndex);
  const results = fuse.search(`"=${query}"`);
  return results
    .sort((a: any, b: any) => {
      return a.score - b.score;
    })
    .map((result: any) => result.item);
};

export const searchProducts = async (storeId: string, query: string) => {
  let products = await fetchAllProducts(storeId);

  const options = {
    keys: [
      {
        name: "title",
        weight: 1,
      },
      {
        name: "uniqueId",
        weight: 1,
      },
    ],
    minMatchCharLength: Math.max(3, (query.length + 1) / 2),
    useExtendedSearch: true,
  };
  const fuseIndex = Fuse.createIndex(options.keys, products);
  const fuse = new Fuse(products, options, fuseIndex);
  const results = fuse.search(`^"${query}" | "${query}"`);
  return results
    .sort((a: any, b: any) => {
      return a.score - b.score;
    })
    .map((result: any) => result.item);
};

export const fetchOrderTags = () => {
  return [];
};

export const fetchCustomerTags = async (storeId: string, channel?: string) => {
  try {
    const channelToUse = channel || CHANNELS.WHATSAPP;
    const source =
      channelToUse === CHANNELS.LIVECHAT ? "MANIFEST_FILTER_BLOCK" : "FILTER_BLOCK";
    const response: any = await RequestExecutor.fetch({
      apiPath: `${getCampaignEndPoints()}/queryBuilderApiFunctions-getFilters`,
      apiMethod: HTTPMethods.POST,
      requestBody: {
        storeId: storeId,
        token: storeId,
        action: "GET_USER_PROPERTY_VALUES",
        filterName: "tags",
        useNew: true,
        source,
      },
    });

    if (response.data) {
      return response.data.response;
    }

    return [];
  } catch (err) {
    captureErrorToSentry(err, "Error in fetchCustomerTags");
    return [];
  }
};

export const SyncFlowTemplateToLibrary = async (
  storeId: string,
  flowId: string
): Promise<SyncTemplateResponse> => {
  try {
    const response: any = await RequestExecutor.fetch({
      apiPath: `${getChatbotEndpoints()}/sync-flow-templates-to-library`,
      apiMethod: HTTPMethods.POST,
      requestBody: {
        store_id: storeId,
        flow_id: flowId,
      },
    });
    if (response.data) {
      return response.data;
    }
    return {};
  } catch (err) {
    captureErrorToSentry(err, "Error in SyncFlowTemplateToLibrary");
    return {};
  }
};

export const createNewFlow = async (storeId: string, flowDetails: any) => {
  try {
    const payload = {
      storeId: storeId,
      ...flowDetails,
    };
    const url = getChatbotEndpoints() + "/create-new-flow";
    const response: any = await RequestExecutor.sendData<{
      data: any;
    }>({
      apiPath: url,
      apiMethod: HTTPMethods.POST,
      requestBody: payload,
    });
    if (response?.data && response.data?.success) {
      return response.data.data;
    }
    return false;
  } catch (err) {
    captureErrorToSentry(err, "Error in createNewFlow");
    return false;
  }
};
